/**
 * "More CLI commands" — CLI section (ADR-0074).
 *
 * Reconciled command-by-command against the CLI's own registration, most
 * recently in the docs surface audit: nine command families shipped with no
 * documentation at all — `status`, `generate`, `integrate`, `embed check`,
 * `catalog` and its subcommands, `governance list`, `approvals`, `keys list`
 * and `drift`. A command a reader cannot discover is a command that does not
 * exist for them, and several of these are the ones that answer the questions
 * the rest of these docs raise ("is my checkout in sync?", "what is my
 * workspace actually serving?").
 *
 * Ordered by when a reader needs them rather than alphabetically: get set up,
 * check what is live, wire the widget in, inspect a catalog, then govern.
 */
import type { DocPageContent } from '../types';

export const CLI_COMMANDS_PAGE: DocPageContent = {
  slug: 'cli/commands',
  titleKey: 'docs.cli.commands.title',
  descriptionKey: 'docs.cli.commands.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.cli.commands.lead' },

    { kind: 'heading', level: 2, id: 'login', textKey: 'docs.cli.commands.login.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.login.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix login\nnpx syncanix logout' },

    // ADR-0436 — `whoami` / `workspace` were on the overview page but had no
    // section here, so the deeper reference omitted the two commands that
    // answer "who am I, and which workspace does the next upload go to".
    { kind: 'heading', level: 2, id: 'identity', textKey: 'docs.cli.commands.identity.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.identity.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix whoami\nnpx syncanix workspace list\nnpx syncanix workspace use acme',
    },

    { kind: 'heading', level: 2, id: 'import', textKey: 'docs.cli.commands.import.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.import.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix import --openapi ./openapi.json\nnpx syncanix import --graphql ./schema.graphql',
    },

    // ── Check what is live ─────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'status', textKey: 'docs.cli.commands.status.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.status.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix status' },

    { kind: 'heading', level: 2, id: 'drift', textKey: 'docs.cli.commands.drift.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.drift.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix drift' },

    { kind: 'heading', level: 2, id: 'embed', textKey: 'docs.cli.commands.embed.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.embed.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix embed check https://app.acme.com' },

    // ── Get the integration written for you ────────────────────────────
    { kind: 'heading', level: 2, id: 'generate', textKey: 'docs.cli.commands.generate.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.generate.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix generate' },

    { kind: 'heading', level: 2, id: 'integrate', textKey: 'docs.cli.commands.integrate.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.integrate.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix integrate scan\nnpx syncanix integrate plan\nnpx syncanix integrate apply',
    },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.cli.commands.integrate.note.title',
      textKey: 'docs.cli.commands.integrate.note.body',
    },

    // ── Inspect a catalog ──────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'catalog', textKey: 'docs.cli.commands.catalog.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.catalog.body' },
    {
      kind: 'code',
      language: 'bash',
      code:
        'npx syncanix catalog list --side-effect destructive\n' +
        'npx syncanix catalog diff ./before.json ./after.json\n' +
        'npx syncanix catalog compliance --format md',
    },
    { kind: 'paragraph', textKey: 'docs.cli.commands.catalog.override' },

    // ── Govern from the terminal ───────────────────────────────────────
    {
      kind: 'heading',
      level: 2,
      id: 'governance',
      textKey: 'docs.cli.commands.governance.heading',
    },
    { kind: 'paragraph', textKey: 'docs.cli.commands.governance.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix governance list\nnpx syncanix approvals\nnpx syncanix keys list',
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.cli.commands.governance.note.title',
      textKey: 'docs.cli.commands.governance.note.body',
      links: {
        review: {
          target: { docSlug: 'dashboard/governance' },
          labelKey: 'docs.cli.commands.governance.note.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'dev', textKey: 'docs.cli.commands.dev.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.dev.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix dev --port 4321' },

    { kind: 'heading', level: 2, id: 'doctor', textKey: 'docs.cli.commands.doctor.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.doctor.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix doctor' },

    { kind: 'heading', level: 2, id: 'byomcp', textKey: 'docs.cli.commands.byomcp.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.byomcp.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix byomcp register --server-url https://mcp.example.com',
    },

    { kind: 'heading', level: 2, id: 'yaml', textKey: 'docs.cli.commands.yaml.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.yaml.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix yaml init\nnpx syncanix yaml validate\nnpx syncanix yaml apply --catalog ./.syncanix/catalog.json',
    },

    { kind: 'heading', level: 2, id: 'version', textKey: 'docs.cli.commands.version.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.commands.version.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix version' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.cli.commands.next.init.title',
          textKey: 'docs.cli.commands.next.init.text',
          to: { docSlug: 'cli/init' },
        },
        {
          titleKey: 'docs.cli.commands.next.mcp.title',
          textKey: 'docs.cli.commands.next.mcp.text',
          to: { docSlug: 'identity' },
        },
      ],
    },
  ],
};
